import { FunctionComponent, useState } from "react";

import { useTranslation } from "react-i18next";

import { faSpinner } from "@fortawesome/pro-solid-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components/Modals/Modal";
import {
	BulkFindPersonRequest,
	BulkFindPersonResponse,
	BulkFindPersonSelection,
} from "@/pages/Private/redux/list/list-find-person.api";
import { FindPeoplePlanState } from "@/pages/Private/redux/list/list-find-person.slice";
import { describeSaveFailure } from "@/utils/describeSaveFailure";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { FindPersonContactModeSelector } from "../../pages/ListEditDetails/components/FindPersonContactModeSelector";
import { SearchProfilePresetBar } from "../../pages/ListEditDetails/components/searchProfilePreset/SearchProfilePresetBar";
import { EnrichmentFilterConfig } from "../../pages/ListEditDetails/ListEditDetails";
import { List } from "../../schema/list";

import { FindPeopleLookupsSection } from "./FindPeopleCostSections";
import {
	buildFindPeopleRequest,
	clampPeoplePerCompany,
	profilesProblem,
	ProfilesProblem,
	seedFromList,
} from "./findPeopleDialog.util";
import { FindPeoplePlanSection, PeoplePerCompanyStepper } from "./FindPeoplePlanSection";
import { FindPeopleResult } from "./FindPeopleResult";
import { RULE_PROBLEM_KEY } from "./profileProblemKeys";
import { SearchProfilesEditor } from "./SearchProfilesEditor";
import { useFindPeopleList, useFindPeoplePlan } from "./useFindPeopleData";

const PREFIX = "defineIcps.bulkFindPeople";

interface FindPeopleDialogProps {
	/** The list the dialog was opened on: the selection's own list. */
	listId: number;
	/** The selection the dialog was opened for; it stays the same while the dialog is open. */
	selection: BulkFindPersonSelection;
	/** The opening's key, which its plan is filed under. */
	sessionKey: string;
	onClose: () => void;
	/** Starts the search; answers what the API answered, or throws its refusal. */
	onSubmit: (request: BulkFindPersonRequest) => Promise<BulkFindPersonResponse>;
}

/**
 * The bulk "Find people" dialog (PREC-4398 slice 3, screen C of PREC-4400's confirmed mockup). It
 * opens once the list's search settings are loaded, so it seeds from loaded data only, and fails
 * closed when they or the plan cannot be read: no start, no quote against a guessed balance. The
 * quote, the lookups and the balance check are the server's plan; the search names that plan and
 * is refused when it changed, and the dialog then checks again.
 */
export const FindPeopleDialog: FunctionComponent<FindPeopleDialogProps> = (props) => {
	const { t } = useTranslation();
	const { list, failed } = useFindPeopleList(props.listId);
	const { plan, replan } = useFindPeoplePlan(props.sessionKey, props.selection);

	if (!list) {
		return (
			<Modal handleClose={props.onClose} isOpened size="md" title={t(`${PREFIX}.titlePlanning`)}>
				{failed ? (
					<p className="text-sm text-status-error" role="alert">
						{t(`${PREFIX}.listError`)}
					</p>
				) : (
					<div className="flex justify-center py-6" data-testid="find-people-loading">
						<Icon className="h-5 w-5 animate-spin text-brand-light" icon={faSpinner} />
					</div>
				)}
			</Modal>
		);
	}

	return <FindPeopleForm {...props} list={list} plan={plan} replan={replan} />;
};

interface FindPeopleFormProps extends FindPeopleDialogProps {
	list: List;
	plan: FindPeoplePlanState;
	replan: () => void;
}

/** The text of a problem that keeps the search from starting. */
function problemKey(problem: ProfilesProblem): { key: string; options?: Record<string, unknown> } {
	switch (problem.kind) {
		case "NO_PROFILE":
			return { key: "noProfile" };
		case "INCOMPLETE_PROFILE":
			return { key: "incompleteProfile" };
		case "INCOMPLETE_RULE":
			return { key: RULE_PROBLEM_KEY[problem.problem], options: { rule: problem.rule } };
	}
}

const isPlanChanged = (error: unknown): boolean =>
	!!error && typeof error === "object" && (error as { status?: unknown }).status === 409;

const FindPeopleForm: FunctionComponent<FindPeopleFormProps> = ({
	list,
	plan,
	replan,
	selection,
	onClose,
	onSubmit,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`${PREFIX}.${key}`, options ?? {});
	/** The Find person mode's copy, shared with the list run's step; the bulk card differs (PREC-4625). */
	const ds = (key: string) => t(`list.detailsStep.contactMode.${key}`);
	const [seed] = useState(() => seedFromList(list));
	const [config, setConfig] = useState<EnrichmentFilterConfig | undefined>(seed.config);
	const [peoplePerCompany, setPeoplePerCompany] = useState(seed.peoplePerCompany);
	const [contactMode, setContactMode] = useState(seed.contactMode);
	const [result, setResult] = useState<BulkFindPersonResponse | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [planChanged, setPlanChanged] = useState(false);
	const { run, isRunning } = useSingleFlight();

	const ready = plan.status === "ready" ? plan.plan : null;
	const companies = ready?.companies ?? 0;
	const quote = ready?.quotes.find((candidate) => candidate.peoplePerCompany === peoplePerCompany);
	const problem = profilesProblem(config);
	const canSubmit = !!ready && !!quote && companies > 0 && !problem && ready.balanceCoversSearch;

	const handleSubmit = () =>
		run(async () => {
			if (!canSubmit || !config || !ready) {
				return;
			}

			setSubmitError(null);
			setPlanChanged(false);

			try {
				setResult(
					await onSubmit(
						buildFindPeopleRequest(selection, config, peoplePerCompany, ready.planKey, contactMode)
					)
				);
			} catch (error) {
				// The server ran nothing: what it would search, or what it would cost, changed since
				// the user checked it. Check again and let the user decide on the new plan.
				if (isPlanChanged(error)) {
					setPlanChanged(true);
					replan();

					return;
				}

				setSubmitError(describeSaveFailure(error, ts("submitError")));
			}
		});

	if (result) {
		return (
			<Modal
				footer={
					<div className="ml-auto">
						<Button color={ButtonColor.BRAND} title={ts("close")} type="button" onClick={onClose} />
					</div>
				}
				handleClose={onClose}
				isOpened
				size="md"
				title={ts("resultTitle")}
			>
				<FindPeopleResult result={result} />
			</Modal>
		);
	}

	const shownProblem = problem ? problemKey(problem) : null;

	return (
		<Modal
			disableSave={!canSubmit}
			handleClose={onClose}
			handleSave={() => void handleSubmit()}
			isLoading={isRunning}
			isOpened
			overflow
			size="md"
			submitButtonText={ready ? ts("submit", { count: companies }) : ts("submitPlanning")}
			subtitle={ts(seed.hasListProfiles ? "subtitle" : "subtitleNoListProfiles")}
			title={ready ? ts("title", { count: companies }) : ts("titlePlanning")}
		>
			<div className="flex flex-col gap-4">
				<SearchProfilePresetBar
					clearedMaxPerCompany={seed.peoplePerCompany}
					listProfiles={
						seed.hasListProfiles
							? {
									label: ts("listProfiles"),
									onSelect: () => {
										const fresh = seedFromList(list);

										setConfig(fresh.config);
										setPeoplePerCompany(fresh.peoplePerCompany);
									},
								}
							: undefined
					}
					maxPerCompany={peoplePerCompany}
					setMaxPerCompany={(count) => setPeoplePerCompany(clampPeoplePerCompany(count))}
					setTitleFilters={setConfig}
					titleFilters={config}
				/>
				<SearchProfilesEditor config={config} list={list} setConfig={setConfig} />
				<PeoplePerCompanyStepper value={peoplePerCompany} onChange={setPeoplePerCompany} />
				{contactMode && (
					<FindPersonContactModeSelector
						contactsFirstDescription={ds("contactsFirstBulkDescription")}
						hint={ts("contactModeHint")}
						value={contactMode}
						onChange={setContactMode}
					/>
				)}
				{ready && (
					<FindPeopleLookupsSection
						isTracker={ready.isTracker}
						listId={list.id}
						lookups={ready.lookups}
					/>
				)}
				{planChanged && (
					<p className="text-ssm font-semibold text-status-warning" role="status">
						{ts("planChanged")}
					</p>
				)}
				<FindPeoplePlanSection peoplePerCompany={peoplePerCompany} plan={plan} retryPlan={replan} />
				{shownProblem && (
					<p className="text-ssm font-semibold text-status-error" role="alert">
						{ts(shownProblem.key, shownProblem.options)}
					</p>
				)}
				{submitError && (
					<p className="text-ssm font-semibold text-status-error" role="alert">
						{submitError}
					</p>
				)}
			</div>
		</Modal>
	);
};
